/**
 * Health Utility Functions
 * Utilities for health log data processing, formatting, and export
 */

import { format, parseISO, differenceInDays, differenceInMinutes } from 'date-fns';
import type { HealthLog, HealthLogType, HealthStats, Medication } from '@/features/health/types/healthTypes';

// ============================================================================
// Color Utilities
// ============================================================================

export interface LogTypeColors {
  bg: string;
  text: string;
  border: string;
  dot: string;
}

const TYPE_COLORS: Record<string, LogTypeColors> = {
  seizure: {
    bg: 'bg-red-50',
    text: 'text-red-600',
    border: 'border-red-200',
    dot: '#EF4444',
  },
  medication: {
    bg: 'bg-blue-50',
    text: 'text-blue-600',
    border: 'border-blue-200',
    dot: '#3B82F6',
  },
  symptom: {
    bg: 'bg-amber-50',
    text: 'text-amber-600',
    border: 'border-amber-200',
    dot: '#F59E0B',
  },
  sleep: {
    bg: 'bg-violet-50',
    text: 'text-violet-600',
    border: 'border-violet-200',
    dot: '#8B5CF6',
  },
  feeding: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-600',
    border: 'border-emerald-200',
    dot: '#10B981',
  },
  therapy: {
    bg: 'bg-sage/10',
    text: 'text-sage-dark',
    border: 'border-sage/30',
    dot: '#7A9E7E',
  },
  vital: {
    bg: 'bg-pink-50',
    text: 'text-pink-600',
    border: 'border-pink-200',
    dot: '#EC4899',
  },
  mood: {
    bg: 'bg-indigo-50',
    text: 'text-indigo-600',
    border: 'border-indigo-200',
    dot: '#6366F1',
  },
};

const DEFAULT_COLORS: LogTypeColors = {
  bg: 'bg-gray-50',
  text: 'text-gray-600',
  border: 'border-gray-200',
  dot: '#8B8178',
};

export function getLogTypeColor(typeSlug: string): LogTypeColors {
  return TYPE_COLORS[typeSlug] || DEFAULT_COLORS;
}

export function getSeverityColor(severity: number): string {
  if (severity >= 8) return '#EF4444'; // red
  if (severity >= 6) return '#F97316'; // orange
  if (severity >= 4) return '#F59E0B'; // amber
  if (severity >= 2) return '#84CC16'; // lime
  return '#10B981'; // emerald
}

export function getSeverityLabel(severity: number): string {
  if (severity >= 8) return 'Severe';
  if (severity >= 6) return 'Moderately Severe';
  if (severity >= 4) return 'Moderate';
  if (severity >= 2) return 'Mild';
  return 'Minimal';
}

// ============================================================================
// Data Formatting
// ============================================================================

export function formatHealthLogData(log: HealthLog): Record<string, any> {
  const formatted: Record<string, any> = {
    'Date/Time': format(parseISO(log.occurredAt), 'MMM d, yyyy h:mm a'),
    'Type': log.logType?.name || 'Unknown',
  };

  if (log.severity !== undefined && log.severity !== null) {
    formatted['Severity'] = `${log.severity}/10 (${getSeverityLabel(log.severity)})`;
  }

  if (log.durationMinutes) {
    formatted['Duration'] = formatDuration(log.durationMinutes);
  }

  if (log.location) {
    formatted['Location'] = log.location;
  }

  // Add type-specific data
  if (log.data) {
    Object.entries(log.data).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        const label = formatFieldLabel(key);
        formatted[label] = formatFieldValue(value);
      }
    });
  }

  if (log.notes) {
    formatted['Notes'] = log.notes;
  }

  return formatted;
}

function formatFieldLabel(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

function formatFieldValue(value: any): string {
  if (Array.isArray(value)) {
    return value.map((v) => formatFieldLabel(String(v))).join(', ');
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  return String(value);
}

export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${remainingMinutes}m`;
}

// ============================================================================
// Statistics Calculations
// ============================================================================

export function calculateHealthStats(
  logs: HealthLog[],
  medications: Medication[] = [],
  dateRange?: { start: Date; end: Date }
): HealthStats {
  const filteredLogs = dateRange
    ? logs.filter((log) => {
        const date = parseISO(log.occurredAt);
        return date >= dateRange.start && date <= dateRange.end;
      })
    : logs;

  // Total entries
  const totalEntries = filteredLogs.length;

  // Therapy sessions
  const therapySessions = filteredLogs.filter(
    (log) => log.logType?.slug === 'therapy'
  ).length;

  // Seizure-free days calculation
  const seizureLogs = filteredLogs.filter(
    (log) => log.logType?.slug === 'seizure'
  );
  const lastSeizure = seizureLogs.length > 0
    ? seizureLogs.sort((a, b) => 
        new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime()
      )[0]
    : null;
  const seizureFreeDays = lastSeizure
    ? differenceInDays(new Date(), parseISO(lastSeizure.occurredAt))
    : dateRange
      ? differenceInDays(dateRange.end, dateRange.start)
      : 0;

  // Medication adherence (simplified calculation)
  const activeMedications = medications.filter((m) => m.isActive);
  const medicationLogs = filteredLogs.filter(
    (log) => log.logType?.slug === 'medication'
  );
  // This is a simplified calculation - real implementation would need dose tracking
  const expectedDoses = activeMedications.length * (dateRange 
    ? differenceInDays(dateRange.end, dateRange.start) 
    : 30);
  const medicationAdherence = expectedDoses > 0
    ? Math.min(Math.round((medicationLogs.length / expectedDoses) * 100), 100)
    : 100;

  // Calculate current logging streak
  const logDates = new Set(
    filteredLogs.map((log) => format(parseISO(log.occurredAt), 'yyyy-MM-dd'))
  );
  let currentStreak = 0;
  let checkDate = new Date();
  while (logDates.has(format(checkDate, 'yyyy-MM-dd'))) {
    currentStreak++;
    checkDate = new Date(checkDate.setDate(checkDate.getDate() - 1));
  }

  // Log type counts
  const logTypeCounts: Record<string, number> = {};
  filteredLogs.forEach((log) => {
    const slug = log.logType?.slug || 'other';
    logTypeCounts[slug] = (logTypeCounts[slug] || 0) + 1;
  });

  return {
    totalEntries,
    therapySessions,
    seizureFreeDays,
    medicationAdherence,
    currentStreak,
    logTypeCounts,
    lastUpdated: new Date().toISOString(),
  };
}

// ============================================================================
// Export Functions
// ============================================================================

export interface ExportOptions {
  format: 'csv' | 'pdf' | 'json';
  dateRange?: { start: Date; end: Date };
  includeNotes?: boolean;
  includeAttachments?: boolean;
  groupBy?: 'date' | 'type' | 'none';
}

export function exportHealthLogs(
  logs: HealthLog[],
  options: ExportOptions
): string | Blob {
  const { format, dateRange, includeNotes = true, groupBy = 'none' } = options;

  // Filter by date range if provided
  let filteredLogs = dateRange
    ? logs.filter((log) => {
        const date = parseISO(log.occurredAt);
        return date >= dateRange.start && date <= dateRange.end;
      })
    : logs;

  // Sort by date
  filteredLogs = filteredLogs.sort(
    (a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime()
  );

  if (format === 'json') {
    return JSON.stringify(filteredLogs, null, 2);
  }

  if (format === 'csv') {
    return generateCSV(filteredLogs, includeNotes);
  }

  // For PDF, return the data structure (actual PDF generation would use a library)
  return JSON.stringify({
    title: 'Health Log Export',
    dateRange: dateRange
      ? `${format(dateRange.start, 'MMM d, yyyy')} - ${format(dateRange.end, 'MMM d, yyyy')}`
      : 'All Time',
    totalEntries: filteredLogs.length,
    logs: filteredLogs.map(formatHealthLogData),
  });
}

function generateCSV(logs: HealthLog[], includeNotes: boolean): string {
  const headers = [
    'Date',
    'Time',
    'Type',
    'Severity',
    'Duration (min)',
    'Location',
    ...(includeNotes ? ['Notes'] : []),
  ];

  const rows = logs.map((log) => {
    const date = parseISO(log.occurredAt);
    return [
      format(date, 'yyyy-MM-dd'),
      format(date, 'HH:mm'),
      log.logType?.name || 'Unknown',
      log.severity?.toString() || '',
      log.durationMinutes?.toString() || '',
      log.location || '',
      ...(includeNotes ? [escapeCSV(log.notes || '')] : []),
    ];
  });

  const csvContent = [
    headers.join(','),
    ...rows.map((row) => row.join(',')),
  ].join('\n');

  return csvContent;
}

function escapeCSV(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export interface ReportOptions {
  title: string;
  dateRange: { start: Date; end: Date };
  childName: string;
  includeCharts?: boolean;
  includeSummary?: boolean;
  providerNote?: string;
}

export function exportHealthReport(
  logs: HealthLog[],
  stats: HealthStats,
  medications: Medication[],
  options: ReportOptions
): Record<string, any> {
  const { title, dateRange, childName, includeSummary = true, providerNote } = options;

  const filteredLogs = logs.filter((log) => {
    const date = parseISO(log.occurredAt);
    return date >= dateRange.start && date <= dateRange.end;
  });

  // Group logs by type
  const logsByType: Record<string, HealthLog[]> = {};
  filteredLogs.forEach((log) => {
    const type = log.logType?.name || 'Other';
    if (!logsByType[type]) {
      logsByType[type] = [];
    }
    logsByType[type].push(log);
  });

  // Calculate type summaries
  const typeSummaries = Object.entries(logsByType).map(([type, typeLogs]) => {
    const severities = typeLogs
      .filter((l) => l.severity !== undefined && l.severity !== null)
      .map((l) => l.severity as number);
    
    return {
      type,
      count: typeLogs.length,
      avgSeverity: severities.length > 0
        ? Math.round((severities.reduce((a, b) => a + b, 0) / severities.length) * 10) / 10
        : null,
      maxSeverity: severities.length > 0 ? Math.max(...severities) : null,
    };
  }).sort((a, b) => b.count - a.count);

  return {
    metadata: {
      title,
      childName,
      dateRange: {
        start: format(dateRange.start, 'MMMM d, yyyy'),
        end: format(dateRange.end, 'MMMM d, yyyy'),
      },
      generatedAt: format(new Date(), 'MMMM d, yyyy h:mm a'),
      totalDays: differenceInDays(dateRange.end, dateRange.start) + 1,
    },
    summary: includeSummary ? {
      totalEntries: filteredLogs.length,
      seizureFreeDays: stats.seizureFreeDays,
      therapySessions: stats.therapySessions,
      medicationAdherence: stats.medicationAdherence,
      activeMedications: medications.filter((m) => m.isActive).length,
    } : null,
    typeSummaries,
    medications: medications.filter((m) => m.isActive).map((med) => ({
      name: med.name,
      dosage: med.dosage,
      frequency: med.frequency,
      prescribingDoctor: med.prescribingDoctor,
    })),
    recentEntries: filteredLogs.slice(0, 20).map((log) => ({
      date: format(parseISO(log.occurredAt), 'MMM d, yyyy h:mm a'),
      type: log.logType?.name || 'Unknown',
      severity: log.severity,
      duration: log.durationMinutes,
      notes: log.notes?.substring(0, 100),
    })),
    providerNote,
  };
}

// ============================================================================
// Download Helpers
// ============================================================================

export function downloadFile(
  content: string | Blob,
  filename: string,
  mimeType: string
): void {
  const blob = content instanceof Blob
    ? content
    : new Blob([content], { type: mimeType });
  
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function generateExportFilename(
  prefix: string,
  format: 'csv' | 'pdf' | 'json',
  dateRange?: { start: Date; end: Date }
): string {
  const timestamp = format(new Date(), 'yyyy-MM-dd');
  const rangeStr = dateRange
    ? `_${format(dateRange.start, 'MMM-d')}-${format(dateRange.end, 'MMM-d')}`
    : '';
  return `${prefix}${rangeStr}_${timestamp}.${format}`;
}

